/**
 * Blog Submission Review Component
 * Admin interface for reviewing and approving/rejecting blog submissions
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { useToast } from './Toast';
import { LoadingSpinner } from './Loading';
import RichTextEditor from './RichTextEditor';
import { ExpandableScreen, ExpandableScreenContent } from './ui/expandable-screen';
import {
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  EnvelopeIcon,
  CalendarIcon,
  BookOpenIcon,
  ChatBubbleLeftRightIcon,
  UserIcon,
  ExclamationTriangleIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

interface AffiliateLink {
  book_title?: string;
  product_title?: string;
  item_title?: string;
  link: string;
}

// Helper to get the display title from any link type
const getLinkTitle = (link: AffiliateLink): string => {
  return link.book_title || link.product_title || link.item_title || 'Untitled Item';
};

// --- Link Matching Logic (Mirrored from BlogPost.tsx) ---

// Normalize book title for matching - handles case, punctuation, and common variations
const normalizeBookTitle = (title: string): string => {
  return title
    .toLowerCase()
    .trim()
    // Normalize apostrophes and quotes
    .replace(/[''`]/g, "'")
    // Normalize hyphens and dashes
    .replace(/[—–-]/g, '-')
    // Remove extra whitespace
    .replace(/\s+/g, ' ')
    // Normalize commas and spacing
    .replace(/,\s*/g, ', ');
};

// Find book title match using intelligent fuzzy matching
const findBookTitleMatch = (text: string, bookTitles: string[]): string | null => {
  if (!text || !bookTitles || bookTitles.length === 0) return null;

  const normalizedText = normalizeBookTitle(text);

  // Strategy 1: Exact normalized match
  for (const title of bookTitles) {
    if (normalizeBookTitle(title) === normalizedText) {
      return title;
    }
  }

  // Strategy 2: Remove apostrophes and compare (Ender's Game = Enders Game)
  const removeApostrophes = (t: string) => normalizeBookTitle(t).replace(/'/g, '');
  const textNoApostrophe = removeApostrophes(text);
  for (const title of bookTitles) {
    if (removeApostrophes(title) === textNoApostrophe) {
      return title;
    }
  }

  // Strategy 3: Word-by-word matching (handle punctuation differences)
  const getWords = (t: string) => {
    return normalizeBookTitle(t)
      .replace(/[.,!?;:()\[\]{}'"]/g, ' ') // Remove punctuation
      .split(/\s+/)
      .filter(w => w.length > 0);
  };

  const textWords = getWords(text);
  for (const title of bookTitles) {
    const titleWords = getWords(title);
    const significantTitleWords = titleWords.filter(w => w.length > 2);
    const significantTextWords = textWords.filter(w => w.length > 2);

    if (significantTitleWords.length === 0 || significantTextWords.length === 0) continue;

    // Count how many title words appear in text
    const matchingWords = significantTitleWords.filter(tw =>
      significantTextWords.some(txt => txt === tw || txt.includes(tw) || tw.includes(txt))
    );

    const reverseMatch = significantTextWords.filter(txt =>
      significantTitleWords.some(tw => txt === tw || txt.includes(tw) || tw.includes(txt))
    );

    const matchRatio = Math.max(
      matchingWords.length / significantTitleWords.length,
      reverseMatch.length / significantTextWords.length
    );

    if (matchRatio >= 0.7) {
      return title;
    }
  }

  // Strategy 4: Core title matching (remove "the", "a", "an")
  const getCoreTitle = (t: string) => {
    return normalizeBookTitle(t)
      .replace(/^(the|a|an)\s+/i, '')
      .replace(/\s+(the|a|an)$/i, '')
      .replace(/'/g, '');
  };

  const coreText = getCoreTitle(text);
  for (const title of bookTitles) {
    const coreTitle = getCoreTitle(title);
    if (coreTitle === coreText ||
      (coreTitle.length > 5 && coreText.includes(coreTitle)) ||
      (coreText.length > 5 && coreTitle.includes(coreText))) {
      return title;
    }
  }

  return null;
};

// Analyze links against content to check if they will be linked correctly
const analyzeLinkHealth = (content: string, links: AffiliateLink[]) => {
  if (!content || !links || links.length === 0) return [];

  // 1. Build the variations map (Exact logic from BlogPost.tsx)
  const bookLinks: Record<string, string> = {};

  links.forEach((link) => {
    const title = getLinkTitle(link);
    if (title && link.link) {
      bookLinks[title] = link.link;

      // Handle apostrophe variations (e.g. "Enders Game" -> "Ender's Game", "Ender's Game")
      // Also handle specific case for "Ender's Game"
      if (title.toLowerCase().includes('ender') && title.toLowerCase().includes('game')) {
        bookLinks["Ender's Game"] = link.link;
        bookLinks["Ender’s Game"] = link.link;
        bookLinks["Enders Game"] = link.link;
      }

      if (!title.includes("'") && !title.includes("’")) {
        // Try adding apostrophe before 's'
        if (title.endsWith("s Game")) {
          const withApostrophe = title.replace("s Game", "'s Game");
          const withSmartApostrophe = title.replace("s Game", "’s Game");
          bookLinks[withApostrophe] = link.link;
          bookLinks[withSmartApostrophe] = link.link;
        }
      }

      // Handle "The Lion, the Witch and the Wardrobe" specific variations
      if (title.toLowerCase().includes('lion') &&
        title.toLowerCase().includes('witch') &&
        title.toLowerCase().includes('wardrobe')) {
        bookLinks['The Lion, the Witch and the Wardrobe'] = link.link;
        bookLinks['Lion, the Witch and the Wardrobe'] = link.link;
        bookLinks['The Lion, The Witch And The Wardrobe'] = link.link;
        bookLinks['the lion, the witch and the wardrobe'] = link.link;
      }

      // General "The" prefix handling
      if (!title.toLowerCase().startsWith('the ')) {
        const withThe = `The ${title}`;
        bookLinks[withThe] = link.link;
        const withTheTitleCase = `The ${title.charAt(0).toUpperCase() + title.slice(1)}`;
        bookLinks[withTheTitleCase] = link.link;
      }

      // Handle apostrophe variations for existing titles
      if (title.includes("'") || title.includes("'")) {
        const withoutApostrophe = title.replace(/['']/g, '');
        bookLinks[withoutApostrophe] = link.link;
      }
    }
  });

  // 2. Check each submitted link
  const allVariations = Object.keys(bookLinks);
  const paragraphs = content.split('\n').filter(p => p.trim().length > 0);

  return links.map(link => {
    // Find all variations that map to this link's URL
    // (This handles the case where "Ender's Game" is found but the submitted title was "Enders Game")
    const variationsForThisLink = allVariations.filter(v => bookLinks[v] === link.link);

    let matchCount = 0;

    // Check every paragraph
    for (const para of paragraphs) {
      // Use the robust matcher
      const matchedTitle = findBookTitleMatch(para, variationsForThisLink);
      if (matchedTitle) {
        matchCount++;
      }
    }

    return {
      title: getLinkTitle(link),
      count: matchCount,
      variations: variationsForThisLink
    };
  });
};

interface BlogSubmission {
  id: string;
  title: string;
  content: string;
  excerpt: string | null;
  author_name: string;
  author_email: string;
  subdomain: string | null;
  amazon_affiliate_links: AffiliateLink[];
  amazon_storefront_id: string | null;
  blog_column: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'published';
  admin_notes: string | null;
  rejected_reason: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
}

export default function BlogSubmissionReview() {
  const { user } = useAuth();
  const { success, error: showError } = useToast();
  const [submissions, setSubmissions] = useState<BlogSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState<BlogSubmission | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'approved' | 'rejected' | 'published'>('all');
  const [selectedColumn, setSelectedColumn] = useState<string>('main');
  const [editableContent, setEditableContent] = useState<string>('');
  const [editableLinks, setEditableLinks] = useState<any[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // Clean content: remove em dashes and normalize line breaks
  const cleanContent = (html: string): string => {
    if (!html) return '';

    // 1. Remove em dashes and similar characters
    let cleaned = html
      .replace(/[—–⸻]/g, ' ') // Replace em dashes, en dashes, and long dashes with space
      .replace(/\s{2,}/g, ' '); // Normalize multiple spaces

    // 2. Clean up line breaks in paragraphs
    // If it's HTML, we need to be careful. TipTap handles most of this, 
    // but we can ensure paragraphs are tidy.
    cleaned = cleaned
      .replace(/<p><\/p>/g, '') // Remove empty paragraphs
      .replace(/<p>\s+<\/p>/g, '') // Remove whitespace-only paragraphs
      .trim();

    return cleaned;
  };

  // Convert plain text with line breaks into HTML paragraphs for proper display
  const convertToHtml = (text: string): string => {
    if (!text) return '';
    // Check if content is already HTML
    if (text.trim().startsWith('<') && text.includes('</')) {
      return text;
    }
    // Convert plain text paragraphs to HTML
    // Split by double line breaks (paragraphs)
    return text
      .split(/\n\n+/)
      .filter(p => p.trim())
      .map(paragraph => {
        const trimmed = paragraph.trim();
        // Detect section headers: short lines (under 60 chars) that don't end with punctuation
        // or lines that look like titles (e.g., "Introduction", "What I Learned")
        const isHeader = trimmed.length < 60 &&
          !trimmed.endsWith('.') &&
          !trimmed.endsWith('?') &&
          !trimmed.endsWith('!') &&
          !trimmed.endsWith(',') &&
          !trimmed.includes('\n');

        if (isHeader) {
          return `<h3>${trimmed}</h3>`;
        }
        // Regular paragraph - join lines with space (CSS handles spacing)
        const cleanedText = trimmed.split(/\n/).map(line => line.trim()).join(' ');
        return `<p>${cleanedText}</p>`;
      })
      .join('');
  };


  const BLOG_COLUMNS = [
    { value: 'main', label: 'Main Blog (The Lost Archives)' },
    { value: 'bookclub', label: 'Book Club' },
    { value: 'gearheads', label: 'GearHeads' },
    { value: 'borderlands', label: 'Edge of the Borderlands' },
    { value: 'science', label: 'Mad Scientists' },
    { value: 'newtheory', label: 'New Theory' }
  ];

  /* const adminStatus = useAdminStatus(); - Removed invalid hook */

  useEffect(() => {
    loadSubmissions();
  }, [filterStatus]);

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('blog_submissions')
        .select('*')
        .order('created_at', { ascending: false });

      if (filterStatus !== 'all') {
        query = query.eq('status', filterStatus);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error loading submissions:', error);
        showError('Failed to load submissions');
        return;
      }

      setSubmissions(data || []);
    } catch (err) {
      console.error('Error loading submissions:', err);
      showError('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRevisions = async (submission: BlogSubmission) => {
    if (!user) {
      showError('You must be logged in');
      return;
    }

    try {
      setIsSaving(true);

      // Map editableLinks back to the database format
      const dbLinks = editableLinks.map(link => ({
        product_title: link.title,
        link: link.url,
        // We could also store the phrase if we add it to the schema, 
        // but for now we follow existing AffiliateLink shape
      }));

      const cleanedHtml = cleanContent(editableContent);

      const { error } = await supabase
        .from('blog_submissions')
        .update({
          content: cleanedHtml,
          amazon_affiliate_links: dbLinks,
          admin_notes: reviewNotes || null,
          blog_column: selectedColumn,
        })
        .eq('id', submission.id);

      if (error) throw error;

      // Update local state to reflect changes
      setEditableContent(cleanedHtml);
      success('Revisions saved successfully');
      loadSubmissions();
    } catch (err: any) {
      console.error('Error saving revisions:', err);
      showError(err.message || 'Failed to save revisions');
    } finally {
      setIsSaving(false);
    }
  };

  const handleApprove = async (submission: BlogSubmission) => {
    if (!user) {
      showError('You must be logged in');
      return;
    }

    try {
      // First, save any pending revisions
      const dbLinks = editableLinks.map(link => ({
        product_title: link.title,
        link: link.url
      }));

      const cleanedHtml = cleanContent(editableContent);

      const { error } = await supabase
        .from('blog_submissions')
        .update({
          status: 'approved',
          content: cleanedHtml,
          amazon_affiliate_links: dbLinks,
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString(),
          admin_notes: reviewNotes || null,
          blog_column: selectedColumn,
        })
        .eq('id', submission.id);

      if (error) throw error;

      // Send approval notification email
      try {
        const emailResponse = await fetch('/api/blog/submission-approved', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            authorEmail: submission.author_email,
            authorName: submission.author_name.trim(),
            articleTitle: submission.title,
          }),
        });

        if (!emailResponse.ok) {
          console.warn('Failed to send approval notification email:', await emailResponse.text());
        }
      } catch (emailError) {
        console.error('Error sending approval notification email:', emailError);
      }

      success('Submission approved successfully. A notification email has been sent to the author.');
      setSelectedSubmission(null);
      setReviewNotes('');
      loadSubmissions();
    } catch (err: any) {
      console.error('Error approving submission:', err);
      showError(err.message || 'Failed to approve submission');
    }
  };

  const handleReject = async (submission: BlogSubmission) => {
    if (!user) {
      showError('You must be logged in');
      return;
    }

    if (!rejectionReason.trim()) {
      showError('Please provide a reason for rejection');
      return;
    }

    try {
      const { error } = await supabase
        .from('blog_submissions')
        .update({
          status: 'rejected',
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString(),
          rejected_reason: rejectionReason,
          admin_notes: reviewNotes || null,
        })
        .eq('id', submission.id);

      if (error) throw error;

      // Send rejection notification email with reason
      try {
        const emailResponse = await fetch('/api/blog/submission-rejected', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            authorEmail: submission.author_email,
            authorName: submission.author_name.trim(),
            articleTitle: submission.title,
            rejectionReason: rejectionReason.trim(),
          }),
        });

        if (!emailResponse.ok) {
          console.warn('Failed to send rejection notification email:', await emailResponse.text());
        }
      } catch (emailError) {
        console.error('Error sending rejection notification email:', emailError);
      }

      success('Submission rejected. A notification email with the rejection reason has been sent to the author.');
      setSelectedSubmission(null);
      setReviewNotes('');
      setRejectionReason('');
      loadSubmissions();
    } catch (err: any) {
      console.error('Error rejecting submission:', err);
      showError(err.message || 'Failed to reject submission');
    }
  };

  const handlePublish = async (submission: BlogSubmission) => {
    if (!user) {
      showError('You must be logged in');
      return;
    }

    if (!confirm('This will create a published blog post. Continue?')) {
      return;
    }

    try {
      // Generate slug from title
      const slug = submission.title
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();

      // Get user_id/author_id from the submission author (NOT the admin)
      // The schema may use either user_id or author_id, and one may be required
      // We need to find the author's user_id from their email or subdomain
      let userId = null;
      let authorId = null;

      // Method 1: If submission has a subdomain, find the user_id from user_subdomains
      if (submission.subdomain) {
        try {
          const { data: subdomainData, error: subdomainError } = await supabase
            .from('user_subdomains')
            .select('user_id')
            .eq('subdomain', submission.subdomain)
            .maybeSingle();

          if (subdomainError) {
            console.warn('Error looking up user_id from subdomain:', subdomainError);
          } else if (subdomainData?.user_id) {
            userId = subdomainData.user_id;
            authorId = subdomainData.user_id;
            console.log(`Found author user_id ${userId} from subdomain ${submission.subdomain}`);
          }
        } catch (err) {
          console.warn('Could not find user_id from subdomain:', err);
        }
      }

      // Method 2: If we still don't have user_id, try to find it from user_roles by email
      if (!userId && submission.author_email) {
        try {
          const { data: roleData, error: roleError } = await supabase
            .from('user_roles')
            .select('user_id')
            .eq('email', submission.author_email)
            .maybeSingle();

          if (roleError) {
            console.warn('Error looking up user_id from user_roles:', roleError);
          } else if (roleData?.user_id) {
            userId = roleData.user_id;
            authorId = roleData.user_id;
            console.log(`Found author user_id ${userId} from email ${submission.author_email} in user_roles`);
          }
        } catch (err) {
          console.warn('Could not find user_id from user_roles:', err);
        }
      }

      // Method 3: Try to find from blog_submissions (if the author has other submissions)
      if (!userId && submission.author_email) {
        try {
          const { data: otherSubmissions, error: submissionsError } = await supabase
            .from('blog_submissions')
            .select('subdomain')
            .eq('author_email', submission.author_email)
            .not('subdomain', 'is', null)
            .limit(1)
            .maybeSingle();

          if (!submissionsError && otherSubmissions?.subdomain) {
            // Try to get user_id from that subdomain
            const { data: subdomainData } = await supabase
              .from('user_subdomains')
              .select('user_id')
              .eq('subdomain', otherSubmissions.subdomain)
              .maybeSingle();

            if (subdomainData?.user_id) {
              userId = subdomainData.user_id;
              authorId = subdomainData.user_id;
              console.log(`Found author user_id ${userId} from other submission's subdomain`);
            }
          }
        } catch (err) {
          console.warn('Could not find user_id from other submissions:', err);
        }
      }

      // If we still don't have user_id, we cannot proceed - user_id is required
      if (!userId) {
        showError(`Could not find user_id for author ${submission.author_email} (subdomain: ${submission.subdomain || 'none'}). Please ensure the author has a subdomain registered or their email is in user_roles.`);
        return;
      }

      // Ensure author_name is set (required)
      if (!submission.author_name || !submission.author_name.trim()) {
        showError('Author name is required. Please update the submission with an author name before publishing.');
        return;
      }

      // Warn if subdomain is missing (post won't show on Book Club page)
      if (!submission.subdomain) {
        const proceed = confirm(
          'Warning: This submission has no subdomain. The post will be published but may not appear on the Book Club page.\n\n' +
          'Posts without subdomains are typically for THE LOST ARCHIVES main blog.\n\n' +
          'Do you want to continue publishing?'
        );
        if (!proceed) {
          return;
        }
      }

      // Use editableContent (the RTE version) if available, otherwise fall back to original
      const contentToUse = editableContent || convertToHtml(submission.content);
      const cleanedContent = cleanContent(contentToUse);

      // Normalize subdomain once for both data and checkQuery
      const normalizedSubdomain = submission.subdomain?.toLowerCase() || null;

      // Map edibleLinks for publication
      const linksForPub = editableLinks.map(link => ({
        product_title: link.title,
        link: link.url
      }));

      // Create blog post with subdomain and Amazon links
      // Build insert object conditionally to handle different schema versions
      const blogPostData: any = {
        title: submission.title,
        slug: slug,
        content: cleanedContent, // Use cleaned content from outer scope or editableContent
        excerpt: submission.excerpt,
        published: true,
        published_at: new Date().toISOString(),
        status: 'published',
        subdomain: normalizedSubdomain, // User subdomain
        submission_id: submission.id, // Store source submission ID
        amazon_affiliate_links: linksForPub.length > 0 ? linksForPub : (submission.amazon_affiliate_links || []),
        amazon_storefront_id: submission.amazon_storefront_id || null, // Store Amazon storefront ID
        blog_column: selectedColumn || submission.blog_column || null, // Use selected column or fallback to submission
        seo_title: null,
        seo_description: submission.excerpt || null,
        seo_keywords: null,
        og_image_url: null,
      }

      // Set both user_id and author_id to the author's user_id
      blogPostData.user_id = userId;
      blogPostData.author_id = authorId || userId;

      // Only include author_name if the column exists (handled via try-catch fallback)
      // The author_name column may not exist in all schema versions
      // We'll try with it first, and if it fails, retry without it

      let blogPost
      let blogError

      // Check if a post with this slug and subdomain already exists (for republishing)
      const checkQuery = supabase.from('blog_posts').select('id').eq('slug', slug);
      if (normalizedSubdomain) {
        checkQuery.eq('subdomain', normalizedSubdomain);
      } else {
        checkQuery.is('subdomain', null);
      }
      const { data: existingPost } = await checkQuery.maybeSingle();

      if (existingPost) {
        console.log(`Found existing post ${existingPost.id}, updating for republish...`);
        // Use update instead of insert to avoid unique constraint violations
        const updateAttempt = await supabase
          .from('blog_posts')
          .update({
            ...blogPostData,
            author_name: submission.author_name.trim(),
          })
          .eq('id', existingPost.id)
          .select()
          .single();

        if (updateAttempt.error) {
          const errorMsg = updateAttempt.error.message || '';
          if (errorMsg.includes('author_name')) {
            // Column might not exist in update either, retry without it
            const retryUpdate = await supabase
              .from('blog_posts')
              .update(blogPostData)
              .eq('id', existingPost.id)
              .select()
              .single();
            blogPost = retryUpdate.data;
            blogError = retryUpdate.error;
          } else {
            blogPost = updateAttempt.data;
            blogError = updateAttempt.error;
          }
        } else {
          blogPost = updateAttempt.data;
          blogError = null;
        }
      } else {
        // Original insert logic for new posts
        // First attempt: try with author_name
        const firstAttempt = await supabase
          .from('blog_posts')
          .insert([{
            ...blogPostData,
            author_name: submission.author_name.trim(), // Store author name for disclosure
          }])
          .select()
          .single()

        if (firstAttempt.error) {
          const errorMsg = firstAttempt.error.message || '';
          const errorCode = firstAttempt.error.code;

          // Handle different error types with appropriate fallbacks
          if (errorMsg.includes('author_name') || errorCode === '42703') {
            // Column doesn't exist - retry without author_name
            console.warn('author_name column not found, retrying without it:', errorMsg)
            const secondAttempt = await supabase
              .from('blog_posts')
              .insert([blogPostData])
              .select()
              .single()

            blogPost = secondAttempt.data
            blogError = secondAttempt.error
          } else if (errorMsg.includes('user_id') && errorMsg.includes('null')) {
            // user_id is required but we don't have it - this should not happen if we did the lookup correctly
            console.error('user_id is required but not found. This should not happen after lookup:', errorMsg)
            // Don't use admin user - fail with error
            blogPost = null
            blogError = firstAttempt.error
          } else if (errorMsg.includes('author_id') && errorMsg.includes('null') && !blogPostData.author_id && blogPostData.user_id) {
            // author_id might be required but we have user_id - try setting author_id to user_id
            console.warn('author_id may be required, setting to user_id:', errorMsg)
            const fourthAttempt = await supabase
              .from('blog_posts')
              .insert([{
                ...blogPostData,
                author_id: blogPostData.user_id,
              }])
              .select()
              .single()

            blogPost = fourthAttempt.data
            blogError = fourthAttempt.error
          } else {
            // Different error, use the original
            blogPost = firstAttempt.data
            blogError = firstAttempt.error
          }
        } else {
          // Success on first attempt
          blogPost = firstAttempt.data
          blogError = null
        }
      }

      if (blogError) {
        console.error('Error creating blog post:', blogError)
        throw blogError
      }

      // Update submission status (email sent timestamp will be set by the API)
      const { error: updateError } = await supabase
        .from('blog_submissions')
        .update({
          status: 'published',
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString(),
          admin_notes: reviewNotes || null,
        })
        .eq('id', submission.id);

      if (updateError) throw updateError;

      const postUrl = submission.subdomain
        ? `/blog/${submission.subdomain}/${slug}`
        : `/thelostarchives/${slug}`;

      const fullPostUrl = `${window.location.origin}${postUrl}`;

      // Send notification email to the author
      try {
        const emailResponse = await fetch('/api/blog/post-published', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            authorEmail: submission.author_email,
            authorName: submission.author_name.trim(),
            postTitle: submission.title,
            postUrl: fullPostUrl,
            submissionId: submission.id, // Pass submission ID to mark email as sent
          }),
        });

        if (!emailResponse.ok) {
          console.warn('Failed to send notification email:', await emailResponse.text());
          // Don't fail the publish if email fails - just log it
        }
      } catch (emailError) {
        console.error('Error sending notification email:', emailError);
        // Don't fail the publish if email fails - just log it
      }

      success(`Article published successfully! ${submission.subdomain ? 'It should appear on the Book Club page.' : 'Note: Without a subdomain, it may not appear on the Book Club page.'} A notification email has been sent to ${submission.author_email}.`);
      setSelectedSubmission(null);
      setReviewNotes('');
      loadSubmissions();

      // Optionally open the published post in a new tab
      setTimeout(() => {
        window.open(postUrl, '_blank');
      }, 1000);
    } catch (err: any) {
      console.error('Error publishing submission:', err);
      showError(err.message || 'Failed to publish article');
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-400/10 text-yellow-400 border-yellow-400/20';
      case 'approved':
        return 'bg-blue-400/10 text-blue-400 border-blue-400/20';
      case 'rejected':
        return 'bg-red-400/10 text-red-400 border-red-400/20';
      case 'published':
        return 'bg-green-400/10 text-green-400 border-green-400/20';
      default:
        return 'bg-white/5 text-white/40 border-white/10';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div data-tour="sub-filters" className="flex flex-wrap gap-2">
        {(['all', 'pending', 'approved', 'rejected', 'published'] as const).map((status) => (
          <button
            key={status}
            onClick={() => setFilterStatus(status)}
            className={`px-4 py-1.5 text-[10px] font-black uppercase tracking-widest transition-all ${filterStatus === status
              ? 'bg-white text-black'
              : 'bg-white/5 text-white/40 hover:bg-white/10'
              }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {/* Submissions List */}
      <div className="bg-black/20 border border-white/5 overflow-hidden">
        {submissions.length === 0 ? (
          <p className="text-white/40 text-[10px] uppercase font-bold text-center py-12 tracking-widest">No submissions found</p>
        ) : (
          <div className="divide-y divide-white/5">
            {submissions.map((submission) => (
              <div
                key={submission.id}
                className="group p-6 hover:bg-white/[0.02] transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-white font-bold text-lg mb-2 group-hover:text-white transition-colors">{submission.title}</h3>
                    <div className="flex items-center gap-4 text-[10px] uppercase tracking-wider text-white/40 mb-4 flex-wrap">
                      <span className="flex items-center gap-1.5">
                        <UserIcon className="w-3.5 h-3.5 opacity-50" />
                        {submission.author_name}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <EnvelopeIcon className="w-3.5 h-3.5 opacity-50" />
                        {submission.author_email}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <CalendarIcon className="w-3.5 h-3.5 opacity-50" />
                        {formatDate(submission.created_at)}
                      </span>
                      <span className={`px-2 py-0.5 border ${getStatusColor(submission.status)} font-black text-[9px]`}>
                        {submission.status.charAt(0).toUpperCase() + submission.status.slice(1)}
                        {submission.status === 'approved' && (
                          <span className="ml-1 text-yellow-500">
                            (NEEDS PUBLISHING)
                          </span>
                        )}
                      </span>
                    </div>
                    {submission.excerpt && (
                      <p className="text-white/70 text-sm mb-2">{submission.excerpt.substring(0, 150)}...</p>
                    )}
                    {submission.amazon_affiliate_links && submission.amazon_affiliate_links.length > 0 && (
                      <div className="flex items-center gap-2 text-xs text-white/50">
                        <BookOpenIcon className="w-3 h-3" />
                        <span>{submission.amazon_affiliate_links.length} affiliate link(s)</span>
                      </div>
                    )}
                    {submission.subdomain && (
                      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/30 mb-1">
                        <span>Subdomain: <span className="text-white/60">{submission.subdomain}</span></span>
                      </div>
                    )}
                    {submission.amazon_storefront_id && (
                      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/30 mb-2">
                        <span>Storefront ID: <span className="text-white/60">{submission.amazon_storefront_id}</span></span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-[10px] text-white/40 mt-1">
                      <span className="px-2 py-0.5 border border-white/10 text-[9px] uppercase tracking-widest font-bold">
                        {BLOG_COLUMNS.find(c => c.value === (submission.blog_column || 'main'))?.label || submission.blog_column || 'Main Blog'}
                      </span>
                    </div>
                  </div>
                  <button data-tour="sub-review"
                    onClick={() => {
                      setSelectedSubmission(submission);
                      setReviewNotes(submission.admin_notes || '');
                      setRejectionReason(submission.rejected_reason || '');
                      setSelectedColumn(submission.blog_column || 'main');
                      setEditableContent(convertToHtml(submission.content || ''));
                      const initialLinks = (submission.amazon_affiliate_links || []).map((link: any) => ({
                        url: link.link,
                        title: link.product_title || link.book_title || link.item_title || 'Untitled',
                        phrase: ''
                      }));
                      setEditableLinks(initialLinks);
                    }}
                    className="ml-4 p-3 bg-white/5 hover:bg-white text-white hover:text-black transition-all flex-shrink-0"
                    title="Review submission"
                  >
                    <EyeIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Review — fullscreen ExpandableScreen pattern */}
      <ExpandableScreen
        isOpen={!!selectedSubmission}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedSubmission(null);
            setReviewNotes('');
            setRejectionReason('');
            setSelectedColumn('main');
          }
        }}
      >
        <ExpandableScreenContent className="overflow-x-hidden">
          <div className="flex-1 overflow-y-auto overflow-x-hidden">
            <div className="max-w-5xl mx-auto w-full px-4 sm:px-8 pt-20 pb-16 space-y-10">
              <div className="flex items-center gap-3">
                <DocumentTextIcon className="w-5 h-5 text-white/40" />
                <h2 className="text-xl font-black uppercase tracking-wide text-white">Review Submission</h2>
              </div>

              {selectedSubmission && (
              <>
              {/* Submission Details */}
              <section>
                <h4 className="text-[10px] font-black uppercase tracking-widest text-white/30 mb-4 flex items-center gap-2">
                  <DocumentTextIcon className="w-3.5 h-3.5" />
                  General Information
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-widest text-white/20 mb-2">Title</label>
                      <p className="text-lg font-bold text-white">{selectedSubmission.title}</p>
                    </div>

                    <div data-tour="sub-column">
                      <label className="block text-[10px] font-black uppercase tracking-widest text-white/20 mb-2">Column Classification</label>
                      <select
                        value={selectedColumn}
                        onChange={(e) => setSelectedColumn(e.target.value)}
                        className="w-full px-4 py-2.5 bg-white/5 border border-white/10 text-white text-sm focus:border-white focus:outline-none transition-colors"
                      >
                        {BLOG_COLUMNS.map(column => (
                          <option key={column.value} value={column.value} className="bg-black">
                            {column.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-widest text-white/20 mb-2">Author</label>
                      <p className="text-white font-bold">{selectedSubmission.author_name}</p>
                      <p className="text-white/40 text-xs font-mono mt-1">{selectedSubmission.author_email}</p>
                    </div>

                    {selectedSubmission.subdomain && (
                      <div>
                        <label className="block text-[10px] font-black uppercase tracking-widest text-white/20 mb-2">Subdomain</label>
                        <p className="text-white font-mono text-sm">{selectedSubmission.subdomain}.thelostandunfounds.com</p>
                      </div>
                    )}
                  </div>
                </div>
              </section>

              <section>
                <h4 className="text-[10px] font-black uppercase tracking-widest text-white/30 mb-6 flex items-center gap-2">
                  <BookOpenIcon className="w-3.5 h-3.5" />
                  Article Content
                </h4>

                <div className="space-y-8">
                  {selectedSubmission.excerpt && (
                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-widest text-white/20 mb-2">Excerpt</label>
                      <p className="text-white/60 text-sm italic border-l border-white/10 pl-4 py-2 bg-white/[0.02]">{selectedSubmission.excerpt}</p>
                    </div>
                  )}

                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest text-white/20 mb-4">Body Content</label>
                    <div className="border border-white/5">
                      <RichTextEditor
                        content={editableContent}
                        initialLinks={editableLinks}
                        onChange={(html, links) => {
                          setEditableContent(html);
                          setEditableLinks(links);
                        }}
                        placeholder="Article content..."
                      />
                    </div>
                  </div>
                </div>
              </section>

              {(selectedSubmission.amazon_storefront_id || (selectedSubmission.amazon_affiliate_links && selectedSubmission.amazon_affiliate_links.length > 0)) && (
                <section>
                  <h4 className="text-[10px] font-black uppercase tracking-widest text-white/30 mb-6 flex items-center gap-2">
                    <CheckCircleIcon className="w-3.5 h-3.5" />
                    Affiliate & Commercial Assets
                  </h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {selectedSubmission.amazon_storefront_id && (
                      <div className="space-y-4">
                        <label className="block text-[10px] font-black uppercase tracking-widest text-white/20">Storefront</label>
                        <div className="p-4 bg-white/5 border border-white/5 space-y-2">
                          <p className="text-white text-sm font-bold">{selectedSubmission.amazon_storefront_id}</p>
                          <a
                            href={`https://www.amazon.com/shop/${selectedSubmission.amazon_storefront_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-white/40 hover:text-white text-[10px] uppercase font-bold tracking-widest block transition-colors"
                          >
                            Verify on Amazon →
                          </a>
                        </div>
                      </div>
                    )}

                    {selectedSubmission.amazon_affiliate_links && selectedSubmission.amazon_affiliate_links.length > 0 && (
                      <div className="space-y-4">
                        <label className="block text-[10px] font-black uppercase tracking-widest text-white/20">Embedded Links</label>
                        <div className="divide-y divide-white/5 border border-white/5">
                          {selectedSubmission.amazon_affiliate_links.map((link, idx) => (
                            <div key={idx} className="p-3 bg-white/[0.02]">
                              <p className="text-white text-[11px] font-bold mb-0.5">{getLinkTitle(link)}</p>
                              <p className="text-white/30 text-[9px] truncate font-mono">{link.link}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Link Health */}
                  {selectedSubmission.amazon_affiliate_links && selectedSubmission.amazon_affiliate_links.length > 0 && (
                    <div className="mt-8">
                      <label className="block text-[10px] font-black uppercase tracking-widest text-white/20 mb-4">Link Detection Health</label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {analyzeLinkHealth(selectedSubmission.content, selectedSubmission.amazon_affiliate_links).map((result, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-white/5 border border-white/5">
                            <span className="text-[10px] font-bold text-white uppercase tracking-wider truncate mr-4">{result.title}</span>
                            <span className={`px-2 py-0.5 text-[9px] font-black uppercase tracking-tighter ${result.count > 0 ? 'text-green-400 bg-green-400/10' : 'text-red-400 bg-red-400/10'}`}>
                              {result.count} Matches
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </section>
              )}

              <section className="pt-10 border-t border-white/5">
                <h4 className="text-[10px] font-black uppercase tracking-widest text-white/30 mb-6 flex items-center gap-2">
                  <ChatBubbleLeftRightIcon className="w-3.5 h-3.5" />
                  Editorial Action
                </h4>

                <div className="space-y-6">
                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest text-white/20 mb-2">Internal Admin Notes</label>
                    <textarea
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white placeholder-white/20 focus:border-white focus:outline-none transition-colors"
                      rows={3}
                      placeholder="Notes for fellow admins..."
                    />
                  </div>

                  {selectedSubmission.status === 'pending' && (
                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-widest text-white/20 mb-2">Author Rejection Feedback</label>
                      <textarea
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        className="w-full px-4 py-3 bg-red-900/5 border border-red-900/20 text-white placeholder-white/20 focus:border-red-500 focus:outline-none transition-colors"
                        rows={2}
                        placeholder="Why is this being rejected? (Author will see this)"
                      />
                    </div>
                  )}
                </div>
              </section>

              <div className="p-6 bg-white/[0.02] border-t border-white/5 flex flex-wrap items-center justify-between gap-4">
                <div className="flex gap-3">
                  <button data-tour="sub-save"
                    onClick={() => handleSaveRevisions(selectedSubmission)}
                    disabled={isSaving}
                    className="px-6 py-2 bg-white/5 hover:bg-white text-white hover:text-black font-black uppercase tracking-widest text-[10px] transition-all disabled:opacity-50"
                  >
                    {isSaving ? 'SAVING...' : 'SAVE REVISIONS'}
                  </button>

                  {selectedSubmission.status === 'pending' && (
                    <>
                      <button data-tour="sub-approve"
                        onClick={() => handleApprove(selectedSubmission)}
                        className="px-6 py-2 bg-blue-500/20 hover:bg-blue-500 text-blue-400 hover:text-white font-black uppercase tracking-widest text-[10px] transition-all"
                      >
                        APPROVE
                      </button>
                      <button data-tour="sub-reject"
                        onClick={() => handleReject(selectedSubmission)}
                        className="px-6 py-2 bg-red-500/20 hover:bg-red-500 text-red-400 hover:text-white font-black uppercase tracking-widest text-[10px] transition-all"
                      >
                        REJECT
                      </button>
                    </>
                  )}

                  {selectedSubmission.status === 'approved' && (
                    <button
                      onClick={() => {
                        const submissionToPublish = { ...selectedSubmission, blog_column: selectedColumn };
                        handlePublish(submissionToPublish);
                      }}
                      className="px-6 py-2 bg-green-500 hover:bg-green-600 text-white font-black uppercase tracking-widest text-[10px] transition-all flex items-center gap-2"
                    >
                      <CheckCircleIcon className="w-4 h-4" />
                      PUBLISH TO BLOG
                    </button>
                  )}
                </div>

                <button
                  onClick={() => {
                    setSelectedSubmission(null);
                    setReviewNotes('');
                    setRejectionReason('');
                    setSelectedColumn('main');
                  }}
                  className="px-6 py-2 bg-white/5 hover:bg-white/10 text-white/40 hover:text-white font-black uppercase tracking-widest text-[10px] transition-all"
                >
                  CLOSE
                </button>
              </div>
              </>
              )}
            </div>
          </div>
        </ExpandableScreenContent>
      </ExpandableScreen>
    </div>
  );
}
